/**
 * PD-15869 — the plain-English sentences the customer portal uses to say
 * how an account is billed, when its next charge falls, and what it is
 * made of.
 *
 * Pure, and pure by construction: the translator is passed IN rather than
 * imported, so the storefront's `t()` supplies the real copy while the
 * tests supply an interpolating stub. Every string that reaches a
 * customer therefore still goes through translation, and the composition
 * rules stay unit-testable without a React tree.
 *
 * The sentences are the ones the ticket specifies verbatim, because they
 * are the whole point of the feature: a customer should be able to read
 * one line and know when money will leave their account.
 */

export type BillingLineTranslator = (
	key: string,
	params?: Record<string, string>,
) => string;

/** Mirrors `ClientBillingMode` without importing the entity into core. */
export type PortalBillingLineMode =
	| 'charge_on_delivery'
	| 'bill_on_terms'
	| 'cycle_billing';

export type PortalBillingLineFrequency = 'weekly' | 'biweekly' | 'monthly';

export interface PortalBillingLineMethod {
	type: string;
	lastFour?: string | null;
	name?: string | null;
}

export interface PortalBillingLineCycle {
	frequency: PortalBillingLineFrequency;
	/** Day of month (1-28) when monthly; day of week (0 = Sunday) otherwise. */
	day?: number | null;
	nextDate?: string | null;
}

export interface PortalBillingLineInput {
	mode?: PortalBillingLineMode | null;
	termsLabel?: string | null;
	collectionMethod?: PortalBillingLineMethod | null;
	cycle?: PortalBillingLineCycle | null;
	billedThrough?: { name?: string | null; nextDate?: string | null } | null;
}

/**
 * 0 = Sunday, matching `payment_schedule.dayOfWeek` and the cycle-cadence
 * helper. Defined here rather than reached for elsewhere because no shared
 * numeric-weekday helper exists; the names are translated at use.
 */
const WEEKDAY_NAMES = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
] as const;

/**
 * Format a `YYYY-MM-DD` cycle date for display.
 *
 * Parsed and rendered in UTC deliberately: these are calendar dates, not
 * instants, and letting the browser localise them turns "billed on the
 * 1st" into "billed on the 31st" for anyone west of Greenwich.
 */
export function formatBillingDate(
	value: string | null | undefined,
	locale?: string,
): string | null {
	if (value == null || value === '') {
		return null;
	}
	const [year, month, day] = value.slice(0, 10).split('-').map(Number);
	if (
		year == null ||
		month == null ||
		day == null ||
		!Number.isFinite(year) ||
		!Number.isFinite(month) ||
		!Number.isFinite(day)
	) {
		return null;
	}
	const date = new Date(Date.UTC(year, month - 1, day));
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return date.toLocaleDateString(locale, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'UTC',
	});
}

/** 1 → "1st", 22 → "22nd". English ordinals; translated by the caller. */
export function ordinalDay(day: number): string {
	const tens = day % 100;
	if (tens >= 11 && tens <= 13) {
		return `${day}th`;
	}
	switch (day % 10) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
}

/**
 * "Visa ending 0005" — the card's own name when it has one, else its type,
 * plus the last four when known. Null when there is no method at all, so
 * the caller drops the "to …" clause rather than printing "to null".
 */
export function buildCollectionMethodLabel(
	method: PortalBillingLineMethod | null | undefined,
	t: BillingLineTranslator,
): string | null {
	if (method == null) {
		return null;
	}
	const name =
		method.name != null && method.name.trim() !== ''
			? method.name.trim()
			: method.type;
	if (name === '') {
		return null;
	}
	if (method.lastFour == null || method.lastFour === '') {
		return t(name);
	}
	return t('{{method}} ending {{lastFour}}', {
		method: t(name),
		lastFour: method.lastFour,
	});
}

/**
 * The single "how you're billed" line for one account, or null when there
 * is nothing worth saying (no mode resolved).
 */
export function buildBillingLine(
	input: PortalBillingLineInput,
	t: BillingLineTranslator,
): string | null {
	// Billed with parent comes first: WHO bills the customer matters more
	// than the mechanism, and the mechanism belongs to the parent anyway.
	if (input.billedThrough != null) {
		return t('Billed through {{account}}', {
			account: t(input.billedThrough.name ?? t('your parent account')),
		});
	}

	const method = buildCollectionMethodLabel(input.collectionMethod, t);

	switch (input.mode) {
		case 'charge_on_delivery':
			return t('Charged when your order is delivered');

		case 'bill_on_terms': {
			const terms =
				input.termsLabel != null && input.termsLabel.trim() !== ''
					? t(input.termsLabel)
					: null;
			if (terms != null && method != null) {
				return t(
					'Each invoice is charged on its due date ({{terms}}) to {{method}}',
					{ terms, method },
				);
			}
			if (terms != null) {
				return t('Each invoice is charged on its due date ({{terms}})', {
					terms,
				});
			}
			if (method != null) {
				return t('Each invoice is charged on its due date to {{method}}', {
					method,
				});
			}
			return t('Each invoice is charged on its due date');
		}

		case 'cycle_billing': {
			const cycle = input.cycle;
			if (cycle == null) {
				return method != null
					? t('Billed on your billing cycle to {{method}}', { method })
					: t('Billed on your billing cycle');
			}

			if (cycle.frequency === 'monthly') {
				const day = cycle.day != null ? t(ordinalDay(cycle.day)) : null;
				if (day == null) {
					return method != null
						? t('Billed monthly to {{method}}', { method })
						: t('Billed monthly');
				}
				return method != null
					? t('Billed on the {{day}} of each month to {{method}}', {
							day,
							method,
						})
					: t('Billed on the {{day}} of each month', { day });
			}

			const weekday =
				cycle.day != null && cycle.day >= 0 && cycle.day <= 6
					? t(WEEKDAY_NAMES[cycle.day] ?? '')
					: null;

			if (cycle.frequency === 'biweekly') {
				if (weekday == null) {
					return method != null
						? t('Billed every two weeks to {{method}}', { method })
						: t('Billed every two weeks');
				}
				return method != null
					? t('Billed every two weeks on {{weekday}} to {{method}}', {
							weekday,
							method,
						})
					: t('Billed every two weeks on {{weekday}}', { weekday });
			}

			if (weekday == null) {
				return method != null
					? t('Billed weekly to {{method}}', { method })
					: t('Billed weekly');
			}
			return method != null
				? t('Billed weekly on {{weekday}} to {{method}}', { weekday, method })
				: t('Billed weekly on {{weekday}}', { weekday });
		}

		default:
			return null;
	}
}

export interface PortalNextChargeLineInput {
	scheduledDate: string;
	amount: number;
	invoiceCount: number;
	childInvoiceCount?: number | null;
}

/**
 * "Sep 1, 2026 · $1,250.00 · 4 invoices (including 2 from your locations)".
 *
 * The currency formatter is injected so the caller keeps using whatever it
 * already uses on that surface — this helper does not introduce a second
 * money format.
 */
export function buildNextChargeLine(
	input: PortalNextChargeLineInput,
	t: BillingLineTranslator,
	formatCurrency: (amount: number) => string,
	locale?: string,
): string {
	const date = formatBillingDate(input.scheduledDate, locale) ?? t('—');
	const invoices =
		input.invoiceCount === 1
			? t('{{count}} invoice', { count: String(input.invoiceCount) })
			: t('{{count}} invoices', { count: String(input.invoiceCount) });

	const base = t('{{date}} · {{amount}} · {{invoices}}', {
		date: t(date),
		amount: formatCurrency(input.amount),
		invoices,
	});

	const childCount = input.childInvoiceCount ?? 0;
	if (childCount <= 0) {
		return base;
	}

	return `${base} ${t('(including {{count}} from your locations)', {
		count: String(childCount),
	})}`;
}
